import type { ApprovalDto, MindChatDraft, RejectReasonCode } from '../services/MendsVisionService';

/**
 * Fonte única dos textos pt-BR da fila de aprovações: status, motivos (status_reason, revalidação,
 * falhas, compromissos), slots faltantes, chips de rejeição e destino das ações do Registrar.
 * Nenhum código cru chega à tela: valor desconhecido vira um texto genérico seguro.
 */

/** info = neutro/ok, wait = depende de você ou do tempo, blocked = parado de propósito, error = falhou. */
export type LabelTone = 'info' | 'wait' | 'blocked' | 'error';

export interface StatusText {
  label: string;
  tone: LabelTone;
}

export interface ReasonText {
  label: string;
  /** Próximo passo em uma frase (com ponto final). */
  hint?: string;
  tone: LabelTone;
}

const TONE_CLASS: Record<LabelTone, string> = {
  info: 'mai-pill-ai',
  wait: 'mai-pill-wait',
  blocked: 'mai-pill-you',
  error: 'mai-pill-high',
};

export function toneClass(tone: LabelTone): string {
  return TONE_CLASS[tone];
}

// ── Status ──────────────────────────────────────────────────────────────────

const STATUS: Record<string, StatusText> = {
  pending: { label: 'Aguardando seu Ok', tone: 'wait' },
  edited: { label: 'Editado — aguardando seu Ok', tone: 'wait' },
  needs_info: { label: 'Aguardando sua resposta', tone: 'wait' },
  executing: { label: 'Em andamento', tone: 'info' },
  approved: { label: 'Aprovado', tone: 'info' },
  rejected: { label: 'Rejeitado', tone: 'blocked' },
  expired: { label: 'Expirou', tone: 'blocked' },
  undone: { label: 'Desfeito', tone: 'blocked' },
  obsolete: { label: 'Desatualizado', tone: 'blocked' },
  conflicted: { label: 'Em conflito', tone: 'error' },
  not_applicable: { label: 'Não entrou na fila', tone: 'info' },
  cancelled: { label: 'Cancelado', tone: 'blocked' },
  superseded: { label: 'Não enviado', tone: 'blocked' },
  failed: { label: 'Falhou', tone: 'error' },
};

export const APPROVAL_STATUSES = Object.keys(STATUS);

const OPEN = new Set(['pending', 'edited', 'needs_info']);

/** Mesmo conjunto de MendsAiApprovalLifecycle.OpenStatuses: aba Pendentes. */
export function isOpenStatus(status: string | null | undefined): boolean {
  return OPEN.has(norm(status));
}

const UNKNOWN_STATUS: StatusText = { label: 'Situação não reconhecida', tone: 'info' };

export function statusText(status: string | null | undefined): StatusText {
  return STATUS[norm(status)] ?? UNKNOWN_STATUS;
}

/** Explicação de um status terminal quando o backend não gravou motivo. */
const STATUS_EXPLAIN: Record<string, ReasonText> = {
  executing: { label: 'A IA está executando agora', hint: 'Atualize em instantes para ver o resultado.', tone: 'info' },
  rejected: { label: 'Você rejeitou esta sugestão', tone: 'blocked' },
  expired: {
    label: 'Expirou sem decisão',
    hint: 'Se ainda fizer sentido, peça uma nova sugestão.',
    tone: 'blocked',
  },
  undone: { label: 'Você desfez esta ação', tone: 'blocked' },
  obsolete: {
    label: 'A situação mudou e esta ação não se aplica mais',
    hint: 'Peça uma nova sugestão se ainda fizer sentido.',
    tone: 'blocked',
  },
  conflicted: {
    label: 'Outra ação mexeu no mesmo contato ao mesmo tempo',
    hint: 'Confira o contato antes de tentar de novo.',
    tone: 'error',
  },
  not_applicable: { label: 'A ação não se aplicava a este contato', tone: 'info' },
  cancelled: { label: 'Cancelada antes de ser executada', tone: 'blocked' },
  superseded: {
    label: 'Não enviado: algo mais novo aconteceu depois desta sugestão',
    hint: 'Refaça a sugestão.',
    tone: 'blocked',
  },
  failed: {
    label: 'Não consegui executar esta ação',
    hint: 'Confira a conversa antes de tentar de novo.',
    tone: 'error',
  },
};

// ── Slots (missing:<slot>) ──────────────────────────────────────────────────

const SLOT: Record<string, string> = {
  contact: 'contato',
  date: 'quando enviar',
  text: 'o texto',
  channel: 'canal',
  context_note: 'o que foi combinado',
  stage: 'a etapa',
  facts: 'os fatos',
  notes: 'a anotação',
  destination: 'para quem transferir',
  agent: 'o agente',
  value: 'o valor',
  ruleName: 'o nome da regra',
  rulePrompt: 'a instrução da regra',
  ruleContent: 'o conteúdo da regra',
  commitmentId: 'o compromisso',
  path: 'o destino',
};

export function slotLabel(slot: string | null | undefined): string {
  return SLOT[(slot ?? '').trim()] ?? 'uma informação';
}

export function missingSlotsText(slots: string[] | null | undefined): string | null {
  const names = (slots ?? []).filter((s) => s && s.trim()).map(slotLabel);
  if (names.length === 0) return null;
  const joined = names.length === 1
    ? names[0]
    : `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`;
  return `Falta: ${joined}`;
}

const MISSING_PREFIX = 'missing:';

// ── Motivos ─────────────────────────────────────────────────────────────────

const RETRY_SOON = 'Tente de novo em instantes.';
const RECREATE = 'Crie de novo com mais detalhes.';
const CHECK_CHAT = 'Confira a conversa antes de tentar de novo.';
const TALK_IN_CHAT = 'Se ainda fizer sentido, fale com o cliente pela conversa.';
const SHADOW: ReasonText = {
  label: 'Modo sombra — a IA só observa e não envia nada',
  hint: 'Mude o nível de autonomia se quiser que ela aja.',
  tone: 'info',
};
const EXECUTION_FAILED: ReasonText = { label: 'A execução falhou', hint: CHECK_CHAT, tone: 'error' };

const REASON: Record<string, ReasonText> = {
  // Revalidação antes de enviar (MendsAiRevalidationReasons)
  customer_replied: {
    label: 'Não enviado: o cliente respondeu depois desta sugestão',
    hint: 'Abra a conversa e responda.',
    tone: 'blocked',
  },
  stage_closed: {
    label: 'Contato já está fechado/perdido',
    hint: 'Se o negócio reabriu, mude a etapa e peça uma nova sugestão.',
    tone: 'blocked',
  },
  recent_follow_up: {
    label: 'Já houve contato nas últimas 24h',
    hint: 'Espere a resposta do cliente antes de insistir.',
    tone: 'blocked',
  },
  commitment_open: {
    label: 'Já existe um retorno combinado com este contato',
    hint: 'Confira o compromisso aberto antes de mandar outra mensagem.',
    tone: 'blocked',
  },
  too_late: { label: 'Passou do horário combinado', hint: TALK_IN_CHAT, tone: 'blocked' },
  contact_unavailable: {
    label: 'Contato sem canal de envio (sem número ou bloqueado)',
    hint: 'Confira o cadastro do contato.',
    tone: 'error',
  },
  context_unavailable: { label: 'Não consegui conferir a conversa agora', hint: RETRY_SOON, tone: 'wait' },
  no_snapshot: {
    label: 'Sem o retrato da conversa de quando foi sugerido — não dá para conferir',
    hint: 'Refaça a sugestão.',
    tone: 'blocked',
  },
  unknown_kind: {
    label: 'A IA não sabe conferir este tipo de ação antes de executar',
    hint: 'Faça esta ação manualmente.',
    tone: 'blocked',
  },
  approval_not_approved: {
    label: 'O Ok foi desfeito ou encerrado antes do envio',
    hint: 'Nada foi enviado.',
    tone: 'blocked',
  },
  // Pergunta ao vendedor (needs_info)
  info_incomplete: {
    label: 'Não consegui completar — não vou perguntar de novo',
    hint: RECREATE,
    tone: 'info',
  },
  info_completed: { label: 'Sua resposta completou a ação', tone: 'info' },
  needs_info_open: {
    label: 'Já existe uma pergunta aberta para este contato',
    hint: 'Responda a pergunta na aba Pendentes.',
    tone: 'wait',
  },
  needs_info_cooldown: {
    label: 'A IA já perguntou isso há poucos dias e não vai perguntar de novo',
    hint: 'Crie a ação já com todos os detalhes.',
    tone: 'info',
  },
  needs_info_too_many_slots: { label: 'Faltam informações demais para uma pergunta só', hint: RECREATE, tone: 'info' },
  needs_info_unavailable: { label: 'Não consegui abrir a pergunta', hint: RECREATE, tone: 'error' },
  invalid_payload: { label: 'A ação veio incompleta', hint: RECREATE, tone: 'error' },
  // Ciclo de vida / execução
  ttl_expired: STATUS_EXPLAIN.expired,
  reversal_failed: {
    label: 'Desfeito, mas não consegui reverter o efeito',
    hint: 'Confira e corrija manualmente.',
    tone: 'error',
  },
  execution_interrupted: { label: 'A execução foi interrompida no meio', hint: CHECK_CHAT, tone: 'error' },
  execution_failed: EXECUTION_FAILED,
  execution_exception: EXECUTION_FAILED,
  compose_failed: { label: 'Não consegui preparar o follow-up agora — tente de novo.', tone: 'error' },
  // Autonomia
  shadow: SHADOW,
  suppressed_autonomy: SHADOW,
  autonomy_shadow: SHADOW,
  autonomy_unavailable: {
    label: 'Não consegui ler o nível de autonomia — nada foi enviado',
    hint: RETRY_SOON,
    tone: 'error',
  },
  // Sweep de follow-up
  memory_unavailable: { label: 'Não consegui ler a memória do contato', hint: 'Tente de novo mais tarde.', tone: 'wait' },
  customer_replied_since_candidate: {
    label: 'O cliente respondeu enquanto a sugestão era preparada',
    hint: 'Abra a conversa e responda.',
    tone: 'blocked',
  },
  open_approval: {
    label: 'Já existe uma sugestão aberta para este contato',
    hint: 'Resolva a que está na aba Pendentes.',
    tone: 'wait',
  },
  idempotent: {
    label: 'Já existe um follow-up deste contato na fila',
    hint: 'Resolva o que está na aba Pendentes.',
    tone: 'wait',
  },
  policy: { label: 'As regras da IA não permitem esta ação agora', tone: 'info' },
  // Compromissos (CommitmentResults)
  approval_expired: { label: 'A aprovação expirou antes do seu Ok', hint: TALK_IN_CHAT, tone: 'blocked' },
  approval_rejected: { label: 'Você rejeitou a aprovação deste retorno', tone: 'blocked' },
  approval_undone: { label: 'Você desfez a aprovação deste retorno', tone: 'blocked' },
  approval_superseded: { label: 'Substituído por algo mais novo', tone: 'blocked' },
  waiting_expired: {
    label: 'Esperou demais por horário ou aprovação e foi encerrado',
    hint: TALK_IN_CHAT,
    tone: 'blocked',
  },
  approved_for_time: { label: 'Aprovado — programado para o horário combinado', tone: 'info' },
  // Rejeição (MendsAiApprovalLifecycle.RejectReasonCodes)
  already_talked: { label: 'Rejeitado: você já falou com o cliente', tone: 'blocked' },
  bad_timing: { label: 'Rejeitado: não era o momento', tone: 'blocked' },
  bad_text: { label: 'Rejeitado: o texto não estava bom', tone: 'blocked' },
  other: { label: 'Rejeitado por outro motivo', tone: 'blocked' },
};

export const KNOWN_REASON_CODES = Object.keys(REASON);

const GENERIC_REASON: ReasonText = { label: 'Sem detalhes sobre o motivo', tone: 'info' };

function knownReason(code: string | null | undefined): ReasonText | null {
  const c = (code ?? '').trim();
  if (!c) return null;
  if (c.startsWith(MISSING_PREFIX)) {
    return {
      label: `Falta: ${slotLabel(c.slice(MISSING_PREFIX.length))}`,
      hint: 'Responda a pergunta para completar.',
      tone: 'wait',
    };
  }
  return REASON[c] ?? null;
}

/** Motivo em pt-BR; null quando não há código; código desconhecido ⇒ texto genérico. */
export function reasonText(code: string | null | undefined): ReasonText | null {
  if (!(code ?? '').trim()) return null;
  return knownReason(code) ?? GENERIC_REASON;
}

/** Junta rótulo e dica numa frase só (para avisos e leitores de tela). */
export function sentence(r: ReasonText): string {
  const label = /[.!?…]$/.test(r.label) ? r.label : `${r.label}.`;
  return r.hint ? `${label} ${r.hint}` : label;
}

// ── Rejeição ────────────────────────────────────────────────────────────────

export const REJECT_REASONS: { code: RejectReasonCode; chip: string }[] = [
  { code: 'already_talked', chip: 'Já falei com o cliente' },
  { code: 'bad_timing', chip: 'Não é o momento' },
  { code: 'bad_text', chip: 'Texto ruim' },
  { code: 'other', chip: 'Outro motivo' },
];

// ── Tipo de ação ────────────────────────────────────────────────────────────

export const ACTION_KIND_LABELS: Record<string, string> = {
  send_message: 'Enviar mensagem',
  create_follow_up: 'Follow-up',
  follow_up: 'Follow-up',
  change_stage: 'Estágio',
  update_memory: 'Memória',
  register_notes: 'Notas',
  refresh_memory: 'Atualizar memória',
  generate_briefing: 'Briefing',
  open_chat: 'Abrir chat',
  navigate: 'Navegar',
  execute_commitment: 'Retorno combinado',
  upsert_follow_up_rule: 'Regra de follow-up',
  transfer_conversation: 'Transferir conversa',
  discount_decision: 'Decisão de desconto',
};

export function actionKindLabel(kind: string | null | undefined): string {
  return ACTION_KIND_LABELS[norm(kind)] ?? 'Ação da IA';
}

// ── Cartão da fila ──────────────────────────────────────────────────────────

const CODE_LIKE = /^[a-z0-9_:.-]+$/;

/** Texto do executor só se for frase; código cru (ex.: execution_interrupted) nunca aparece. */
export function safeResultText(text: string | null | undefined): string | null {
  const t = (text ?? '').trim();
  if (!t || CODE_LIKE.test(t)) return null;
  return t;
}

const SENDS_AT = /sai em (\d{2}\/\d{2} às \d{2}:\d{2})/i;

export function approvedForTimeLabel(when?: string | null): string {
  return when ? `Aprovado — programado para ${when}` : REASON.approved_for_time.label;
}

export interface ApprovalExplanation {
  status: StatusText;
  /** Por que está assim e o que fazer; null quando não há o que explicar. */
  reason: ReasonText | null;
  /** Frase pt-BR do executor (falha), quando não repete o motivo. */
  detail: string | null;
  /** Resultado de sucesso (caixa verde); null quando não houve sucesso a mostrar. */
  result: string | null;
}

const NOT_SENT = /^não enviado/i;

export function explainApproval(
  item: Pick<ApprovalDto, 'status' | 'statusReason' | 'actionKind' | 'executionResult' | 'missingSlots'>,
): ApprovalExplanation {
  const status = norm(item.status);
  const base = statusText(status);
  const code = (item.statusReason ?? '').trim();

  if (OPEN.has(status)) {
    const missing = status === 'needs_info' ? missingSlotsText(item.missingSlots) : null;
    const reason = missing
      ? { label: missing, tone: 'wait' as const }
      : knownReason(code);
    return { status: base, reason, detail: null, result: null };
  }

  if (status === 'approved') {
    const text = safeResultText(item.executionResult);
    // Ok dado antes do horário e o disparo foi barrado depois (sombra, too_late…): o status fica
    // approved, mas nada saiu. Nunca é sucesso.
    if (text && NOT_SENT.test(text)) {
      return {
        status: { label: 'Aprovado, mas não enviado', tone: 'blocked' },
        reason: { label: text, hint: 'Fale com o cliente pelo chat.', tone: 'blocked' },
        detail: null,
        result: null,
      };
    }
    const when = SENDS_AT.exec(item.executionResult ?? '')?.[1] ?? null;
    if (when || code === 'approved_for_time') {
      return { status: { label: approvedForTimeLabel(when), tone: 'info' }, reason: null, detail: null, result: null };
    }
    return {
      status: base,
      reason: code === 'reversal_failed' ? REASON.reversal_failed : null,
      detail: null,
      result: text,
    };
  }

  const reason = code ? reasonText(code) : STATUS_EXPLAIN[status] ?? null;
  const detail = status === 'failed' ? safeResultText(item.executionResult) : null;
  return { status: base, reason, detail, result: null };
}

/** "Expira em 5 h" a partir de expiresAt; null sem data válida. */
export function expiryText(expiresAt: string | null | undefined, nowMs: number): string | null {
  if (!expiresAt) return null;
  const at = Date.parse(expiresAt);
  if (Number.isNaN(at)) return null;
  const minutes = Math.floor((at - nowMs) / 60_000);
  if (at <= nowMs) return 'Prazo vencido — esta sugestão vai sair da fila';
  if (minutes < 60) return `Expira em ${Math.max(1, minutes)} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `Expira em ${hours} h`;
  return `Expira em ${Math.floor(hours / 24)} dias`;
}

/** 409 do Ok (code SUPERSEDED | CONTEXT_UNVERIFIED + reason). Nada foi enviado em nenhum dos dois. */
export function approveConflictText(code: string | null | undefined, reason: string | null | undefined): ReasonText {
  const known = knownReason(reason);
  if ((code ?? '').toUpperCase() === 'SUPERSEDED') {
    const r = known ?? STATUS_EXPLAIN.superseded;
    return { label: r.label, hint: r.hint ?? 'Refaça a sugestão.', tone: 'blocked' };
  }
  const permanent = PERMANENT_BLOCK[(reason ?? '').trim()];
  if (known && permanent) {
    return { label: notSent(known.label), hint: `Continua pendente, mas ${permanent}`, tone: 'blocked' };
  }
  if ((reason ?? '').trim() === 'context_unavailable') {
    return {
      label: 'Não enviado: não consegui conferir a conversa agora',
      hint: 'Continua pendente — tente de novo em instantes.',
      tone: 'wait',
    };
  }
  // Motivo ausente ou novo: não promete que uma nova tentativa resolve.
  return {
    label: known ? notSent(known.label) : 'Não enviado: não consegui conferir a conversa',
    hint: 'Continua pendente. Se acontecer de novo, rejeite e refaça a sugestão.',
    tone: 'blocked',
  };
}

/**
 * Bloqueios que uma nova tentativa nunca resolve (o claim volta para pending com o motivo anterior):
 * a frase diz o que fazer em vez de "tente de novo".
 */
const PERMANENT_BLOCK: Record<string, string> = {
  unknown_kind: 'isso não se resolve sozinho — faça esta ação manualmente e rejeite a sugestão.',
  no_snapshot: 'isso não se resolve sozinho — rejeite e refaça a sugestão.',
  too_late: 'isso não se resolve sozinho — rejeite a sugestão e, se ainda fizer sentido, fale com o cliente pelo chat.',
  contact_unavailable: 'isso não se resolve sozinho — corrija o cadastro do contato ou rejeite a sugestão.',
};

const LEAKS_CODE = /'[^']*'|\b[a-z]+_[a-z_]+\b|\bapproval\b|\bpayload\b/i;
/** Mensagens técnicas em inglês do controller ("Not found", "MendsVision disabled"…). */
const ENGLISH = /\b(not found|disabled|invalid|error|failed|exception|required|unauthorized|forbidden)\b/i;

/**
 * Mensagem de erro do backend para a tela: frase pt-BR passa; texto com status/código cru
 * ("status 'superseded'", "payload inválido", snake_case) vira o texto padrão do chamador.
 */
export function friendlyApiMessage(message: string | null | undefined, fallback: string): string {
  const m = (message ?? '').trim();
  if (!m) return fallback;
  if (/^approval expirad/i.test(m)) return 'Esta sugestão expirou — nada foi enviado.';
  return LEAKS_CODE.test(m) || ENGLISH.test(m) ? fallback : m;
}

const ANSWER_ERRORS: Record<string, string> = {
  NOT_AWAITING_ANSWER: 'Esta pergunta já foi respondida ou encerrada. A fila foi atualizada.',
  ANSWER_CONFLICT: 'Outra resposta chegou ao mesmo tempo. A fila foi atualizada.',
};

/** Erro de POST /approvals/{id}/answer (400 ANSWER_EMPTY, 409 NOT_AWAITING_ANSWER | ANSWER_CONFLICT | NEEDS_INFO_EXPIRED). */
export function answerErrorText(code: string | null | undefined, message: string | null | undefined): string {
  return ANSWER_ERRORS[(code ?? '').toUpperCase()]
    ?? friendlyApiMessage(message, 'Não consegui registrar a resposta. Tente de novo.');
}

function notSent(label: string): string {
  return label.startsWith('Não enviado') ? label : `Não enviado: ${lowerFirst(label)}`;
}

// ── PDF da proposta no rascunho do chat ─────────────────────────────────────

/**
 * Linha do card de rascunho sobre o PDF. Os campos faltantes são as chaves do modelo do setor
 * (definidas pelo cliente), então aparecem como o backend os manda. null = sem plano de PDF.
 */
export function pdfPlanText(pdf: MindChatDraft['pdf']): string | null {
  if (!pdf) return null;
  switch (pdf.status) {
    case 'available':
      return pdf.templateName
        ? `PDF do modelo '${pdf.templateName}' será enviado junto`
        : 'PDF do modelo será enviado junto';
    case 'missing_fields': {
      const missing = pdf.missingFields ?? [];
      return missing.length > 0
        ? `PDF não será enviado: faltam ${missing.join(', ')}`
        : 'PDF não será enviado: faltam dados do modelo.';
    }
    case 'not_configured':
      return 'Setor sem modelo de PDF: segue só o texto';
    case 'blocked':
      return 'PDF indisponível neste período: segue só o texto.';
    default:
      return null;
  }
}

// ── Criação sem fila (createApprovalOutcome) ────────────────────────────────

export const RETRY_MESSAGE = REASON.compose_failed.label;
export const SHADOW_MESSAGE = 'A autonomia deste setor está em modo sombra: nada foi criado.';
export const QUESTION_OPEN_MESSAGE =
  'A IA já tem uma pergunta aberta sobre este contato — responda na fila de aprovações.';
export const ALREADY_QUEUED_MESSAGE = 'Já existe um follow-up deste contato na fila.';
export const POLICY_MESSAGE = 'Nada foi criado: as regras da IA não permitem esta ação agora.';
export const GENERIC_NOT_CREATED_MESSAGE = 'Nada foi criado: a ação não entrou na fila de aprovações.';

/** Mensagem para um POST /approvals que não criou nada (id 0 / not_applicable). */
export function notCreatedText(code: string | null | undefined): string {
  const c = (code ?? '').trim();
  if (c === 'compose_failed' || c === 'context_unavailable') return RETRY_MESSAGE;
  if (REASON[c] === SHADOW) return SHADOW_MESSAGE;
  if (c === 'needs_info_open') return QUESTION_OPEN_MESSAGE;
  if (c === 'idempotent' || c === 'open_approval') return ALREADY_QUEUED_MESSAGE;
  if (c === 'policy') return POLICY_MESSAGE;
  const known = knownReason(c);
  if (!known) return GENERIC_NOT_CREATED_MESSAGE;
  return `Nada foi criado: ${sentence({ ...known, label: lowerFirst(known.label) })}`;
}

function lowerFirst(s: string): string {
  // Mantém siglas ("IA", "WhatsApp") e só baixa a primeira letra de palavra comum.
  if (/^[A-ZÀ-Ú]{2}/.test(s)) return s;
  return s.charAt(0).toLowerCase() + s.slice(1);
}

function norm(v: string | null | undefined): string {
  return (v ?? '').trim().toLowerCase();
}
